/**
 * Display rules for the soft facts on a course (student count, facilitator).
 *
 * Shared because they drifted once: CourseDetail suppressed a low student count
 * and honoured `show_facilitator`, the signed-in CourseCard dropped both fields
 * outright, and PublicCourseCard — the one surface a stranger sees first — kept
 * showing every course as "★ 0.0 · 2 students · INSTRUCTOR Unknown".
 */

/**
 * Below this, a student count reads as evidence nobody is here rather than as
 * social proof, so it is worth less than the space it takes. It is also not a
 * live number: `courses.total_students` is only ever written by seed
 * migrations (the runtime incrementer was never applied to prod), so on a young
 * tenant it is closer to noise than to a count.
 */
export const STUDENT_COUNT_THRESHOLD = 5;

export const hasMeaningfulStudentCount = (totalStudents?: number | null): boolean =>
  (totalStudents ?? 0) >= STUDENT_COUNT_THRESHOLD;

/**
 * The facilitator credit is per-course opt-in (`courses.show_facilitator`,
 * default off) AND needs a name to show. The name matters as its own condition:
 * anonymous visitors have no read access to `users` at all, so the instructor
 * join comes back null for every logged-out visitor on every tenant. A course
 * that opts in still renders nothing rather than a fabricated "Unknown".
 */
export const showsFacilitator = (course: {
  showFacilitator?: boolean;
  instructorName?: string;
}): boolean => !!course.showFacilitator && !!course.instructorName;
